import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import RecordDialog from "../components/RecordDialog";
import DumperInspectionDialog from "../components/DumperInspectionDialog";
import DumperRemovalDialog from "../components/DumperRemovalDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Minus, ClipboardText, MapPin, PencilSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const BTNS = [
  { action: "fit", label: "FIT TYRE", icon: Plus, color: "bg-emerald-500 text-zinc-950" },
  { action: "remove", label: "REMOVE TYRE", icon: Minus, color: "bg-rose-500 text-zinc-950" },
  { action: "inspect", label: "INSPECTION", icon: ClipboardText, color: "bg-sky-500 text-zinc-950" },
];

export default function Technician() {
  const { currentSite, setCurrentSite, hasRole } = useAuth();
  const [sites, setSites] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [names, setNames] = useState({});
  const [newSite, setNewSite] = useState("");

  const load = () => api.get("/sites").then(({ data }) => {
    setSites(data);
    setNames(Object.fromEntries(data.map((s) => [s.id, s.name])));
    if (currentSite && !data.find((s) => s.id === currentSite.id)) setCurrentSite(null);
  });
  useEffect(() => { load(); }, []);

  const addSite = async () => {
    if (!newSite.trim()) { toast.error("Enter a site name"); return; }
    try {
      await api.post("/sites", { name: newSite.trim() });
      toast.success("Mine site added");
      setNewSite("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add site"); }
  };

  const saveName = async (id) => {
    try {
      await api.put(`/sites/${id}`, { name: names[id] });
      toast.success("Site renamed");
      if (currentSite?.id === id) setCurrentSite({ id, name: names[id] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Technician Hub</h1>
        <p className="mt-1 text-sm text-zinc-400">Select your site for today, then tap an action.</p>
      </div>

      {/* Site selector */}
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 overline text-amber-500"><MapPin size={14} /> Which site are you at today?</div>
          {hasRole("admin") && (
            <button data-testid="edit-sites-toggle" onClick={() => setEditing(!editing)}
              className="flex items-center gap-1 text-xs text-zinc-400 hover:text-amber-500">
              <PencilSimple size={14} /> {editing ? "Done" : "Manage sites"}
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-2" data-testid="site-editor">
            {sites.map((s) => (
              <div key={s.id} className="flex gap-2">
                <Input data-testid={`site-name-${s.id}`} value={names[s.id] ?? ""} className="bg-zinc-950"
                  onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))} />
                <Button size="sm" data-testid={`save-site-${s.id}`} onClick={() => saveName(s.id)}
                  className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400"><Check size={16} /></Button>
              </div>
            ))}
            {sites.length === 0 && <p className="text-xs text-zinc-500">No mine sites for this customer yet — add one below.</p>}
            <div className="flex gap-2 border-t border-zinc-800 pt-3">
              <Input data-testid="new-site-name" placeholder="New mine site name" value={newSite} className="bg-zinc-950"
                autoComplete="off" name="new-mine-site" onChange={(e) => setNewSite(e.target.value)} />
              <Button size="sm" data-testid="add-site-btn" onClick={addSite}
                className="bg-emerald-500 font-bold text-zinc-950 hover:bg-emerald-400"><Plus size={16} weight="bold" /></Button>
            </div>
          </div>
        ) : (
          <Select value={currentSite?.id || ""} onValueChange={(v) => {
            const s = sites.find((x) => x.id === v);
            setCurrentSite(s ? { id: s.id, name: s.name } : null);
          }}>
            <SelectTrigger data-testid="site-selector" className="h-12 bg-zinc-950 text-base"><SelectValue placeholder="Select your mine site" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {BTNS.map((b) => (
          <button key={b.action} data-testid={`tech-${b.action}`} onClick={() => setActive(b.action)}
            className={`flex h-32 flex-col items-center justify-center gap-3 rounded-md font-display text-sm font-extrabold tracking-tight transition-all duration-150 hover:brightness-110 active:scale-[0.98] ${b.color}`}>
            <b.icon size={40} weight="bold" />
            {b.label}
          </button>
        ))}
      </div>

      {active && active !== "inspect" && active !== "remove" && (
        <RecordDialog action={active} open={!!active} siteId={currentSite?.id}
          onOpenChange={(o) => !o && setActive(null)} onDone={() => setActive(null)} />
      )}
      {active === "inspect" && (
        <DumperInspectionDialog open siteId={currentSite?.id} onOpenChange={(o) => !o && setActive(null)} onDone={() => setActive(null)} />
      )}
      {active === "remove" && (
        <DumperRemovalDialog open siteId={currentSite?.id} onOpenChange={(o) => !o && setActive(null)} onDone={() => setActive(null)} />
      )}
    </div>
  );
}
